import { useEffect, useState } from "react";
import api from "../../lib/api";
import { Link } from "react-router-dom";
import { Calendar, TrendUp, MapPin } from "@phosphor-icons/react";

/**
 * CalendarSyncSubscribersTile — admin telemetry for the consumer
 * calendar-sync feature. Shows total subscribers, 7/30-day trend,
 * and top trucks by calendar reach (followers with sync enabled).
 *
 * The "top reach" list doubles as a Pro-upgrade-candidate signal —
 * trucks with high calendar reach have customers actively planning
 * around them; if they're still Free, they're a great target for a
 * targeted upgrade pitch.
 */
export default function CalendarSyncSubscribersTile() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get("/admin/kpis/calendar-sync-subscribers")
      .then((res) => { if (!cancelled) setData(res.data || null); })
      .catch((e) => { if (!cancelled) setErr(e?.response?.data?.detail || e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  return (
    <div data-testid="admin-calendar-sync-subscribers">
      {err && (
        <p className="text-sm text-rose-600 mb-2">{err}</p>
      )}
      {loading && !data ? (
        <div className="text-xs text-muted-foreground italic">Loading…</div>
      ) : data ? (
        <div className="space-y-4">
          {/* Top stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Active subscribers" value={data.enabled_count} testId="cal-sub-active" />
            <Stat label="Paused" value={data.minted_but_paused} testId="cal-sub-paused"
              hint="Token generated but currently disabled" />
            <Stat label="New in 7 days" value={data.new_subscribers_7d} testId="cal-sub-7d"
              icon={<TrendUp weight="duotone" className="h-3.5 w-3.5 text-emerald-500" />} />
            <Stat label="New in 30 days" value={data.new_subscribers_30d} testId="cal-sub-30d"
              icon={<TrendUp weight="duotone" className="h-3.5 w-3.5 text-emerald-500" />} />
          </div>

          {/* Top reach trucks */}
          <div className="rounded-xl border border-border bg-card overflow-hidden">
            <div className="px-3 py-2.5 border-b border-border bg-muted/30">
              <div className="flex items-center gap-1.5">
                <MapPin weight="duotone" className="h-4 w-4 text-primary" />
                <span className="text-sm font-semibold">Top trucks by calendar reach</span>
              </div>
              <p className="text-[11px] text-muted-foreground mt-0.5">
                How many sync-enabled customers have these trucks in their calendar feed.
                Free trucks with high reach are great Pro-upgrade targets.
              </p>
            </div>
            {(data.top_reach_trucks || []).length === 0 ? (
              <div className="px-3 py-6 text-xs text-muted-foreground italic text-center">
                No subscribers with followed trucks yet.
              </div>
            ) : (
              <ul className="divide-y divide-border max-h-72 overflow-y-auto">
                {data.top_reach_trucks.map((t, i) => (
                  <li
                    key={t.truck_id}
                    className="px-3 py-2 flex items-center gap-2"
                    data-testid={`cal-sub-reach-${i}`}
                  >
                    <span className="font-mono text-xs text-muted-foreground tabular-nums w-6 flex-none">
                      #{i + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold truncate flex items-center gap-1.5">
                        {t.truck_slug ? (
                          <Link to={`/t/${t.truck_slug}`} target="_blank" rel="noreferrer" className="hover:underline">
                            {t.truck_name}
                          </Link>
                        ) : (
                          t.truck_name
                        )}
                        {t.is_pro ? (
                          <span className="text-[9px] font-bold tracking-wider uppercase text-primary border border-primary/30 rounded px-1">
                            Pro
                          </span>
                        ) : (
                          <span className="text-[9px] font-bold tracking-wider uppercase text-amber-700 dark:text-amber-300 border border-amber-300 rounded px-1">
                            Free
                          </span>
                        )}
                      </div>
                    </div>
                    <span className="text-xs tabular-nums font-semibold flex-none">
                      {t.reach.toLocaleString()} <span className="text-muted-foreground font-normal">in calendars</span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="text-[11px] text-muted-foreground flex items-start gap-1.5 pt-2 border-t border-border">
            <Calendar weight="duotone" className="h-3.5 w-3.5 flex-none mt-0.5" />
            <span>
              Subscribers count followers with <code>cal_sync_enabled=true</code> AND a feed token.
              &quot;Paused&quot; means they enabled it once and then turned it off — still in your audience for re-engagement.
              Reach counts each follower-truck pair once.
            </span>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function Stat({ label, value, hint, icon, testId }) {
  return (
    <div className="rounded-xl border border-border bg-card p-3" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground flex items-center gap-1">
        {icon}
        {label}
      </div>
      <div className="text-2xl font-display font-bold tabular-nums mt-0.5">
        {(value || 0).toLocaleString()}
      </div>
      {hint && (
        <div className="text-[10.5px] text-muted-foreground mt-0.5 leading-tight">
          {hint}
        </div>
      )}
    </div>
  );
}
